
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Eye, EyeOff } from 'lucide-react';

interface FormFieldWithValidationProps {
  id: string;
  name: string;
  type: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: React.ReactNode;
  validation?: {
    required?: boolean;
    email?: boolean;
    minLength?: number;
    pattern?: RegExp;
    customValidator?: (value: string) => string | null;
  };
  showPasswordToggle?: boolean;
  className?: string;
  autoFocus?: boolean;
}

export const FormFieldWithValidation: React.FC<FormFieldWithValidationProps> = ({
  id,
  name,
  type: initialType,
  label,
  placeholder,
  value,
  onChange,
  icon,
  validation,
  showPasswordToggle = false,
  className,
  autoFocus = false
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const type = showPasswordToggle && showPassword ? 'text' : initialType;

  const validateField = (value: string): string | null => {
    if (!validation) return null;

    if (validation.required && !value.trim()) {
      return `${label} is required`;
    }

    if (validation.email && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'Please enter a valid email address';
    }

    if (validation.minLength && value && value.length < validation.minLength) {
      return `${label} must be at least ${validation.minLength} characters`;
    }

    if (validation.pattern && value && !validation.pattern.test(value)) {
      return `Please enter a valid ${label.toLowerCase()}`;
    }

    if (validation.customValidator && value) {
      return validation.customValidator(value);
    }

    return null;
  };

  useEffect(() => {
    if (touched) {
      setError(validateField(value));
    }
  }, [value, touched]);

  const handleBlur = () => {
    setTouched(true);
    setError(validateField(value));
  };

  const hasError = touched && error;

  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={id} className="text-foreground">
        {label}
      </Label>
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-3 h-4 w-4 text-muted-foreground">
            {icon}
          </div>
        )}
        <Input
          id={id}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
          className={cn(
            icon ? "pl-10" : "",
            showPasswordToggle ? "pr-10" : "",
            hasError ? "border-red-500 focus-visible:ring-red-500" : "",
            "transition-all duration-200"
          )}
          aria-invalid={hasError ? true : false}
          aria-describedby={hasError ? `${id}-error` : undefined}
          autoFocus={autoFocus}
          required={validation?.required}
        />
        {showPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff /> : <Eye />}
          </button>
        )}
      </div>
      {hasError && (
        <p
          id={`${id}-error`}
          className="text-sm text-red-500 animate-fade-in"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};
